import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { nameof } from 'ts-simple-nameof';
import { Roles } from '../../../core/ucr-security/roles';
import { UcrSecurityService } from '../../../core/ucr-security/ucr-security.service';
import { UserInfo } from '../../../core/ucr-security/user-info';
import { DialogService } from '../../../dialog/dialog.service';
import { RosterComponent } from '../../../roster/roster.component';
import { CourseDialogData } from '../../../roster/roster.types';
import { ViewLogComponent } from '../../../roster/view-log/view-log.component';
import { CourseService } from '../../../shared/course.service';
import { Course } from '../../../shared/interface/course.interface';
import { RowUiService } from '../../../shared/row-ui.service';
import { nameofExt } from '../../../shared/services/nameof-extend';
import {
    ActionButtonClickEvent,
    ColumnClickEvent,
    ColumnDefinition,
    FilterSearchCriteria,
    TableFilterItem,
    TableSearchCriteria
} from '../../../tables/table.types';
import { CourseListRegistrarInstructorComponent } from './course-list-registrar-instructor/course-list-registrar-instructor.component';

@Component({
    selector: 'app-registrar-staff-course-list-registrar',
    templateUrl: './course-list-registrar.component.html',
    styleUrls: ['./course-list-registrar.component.scss']
})
export class CourseListRegistrarComponent implements OnInit, OnDestroy {
    tableName: string = 'courses';
    baseUrl: string = 'course';
    isRegAdminStaff: boolean = false;
    filters: Array<TableFilterItem> = [

        {
            property: nameof<Course>((m) => m.termCode),
            inputType: 'multiselect',
            selectOptions: this.service.selectListPost({ q: 'terms' })
        },
        {
            property: nameof<Course>((m) => m.crn),
            label: 'CRN',
            inputType: 'input'
        },
        {
            property: nameof<Course>((m) => m.gradeSubmitted),
            inputType: 'select',
            clearButton: true,
            selectOptions: of([
                {
                    text: 'Yes',
                    value: 'YES'
                },
                {
                    text: 'No',
                    value: 'NO'
                }
            ])
        },
        {
            property: nameof<Course>((m) => m.department),
            inputType: 'input'
        },
        {
            property: nameof<Course>((m) => m.subjectCode),
            inputType: 'input'
        },
        {
            property: nameof<Course>((m) => m.courseNumber),
            inputType: 'input'
        },
        {
            property: nameof<Course>((m) => m.sectionNumber),
            inputType: 'input'
        },
        {
            property: nameof<Course>((m) => m.courseTitle),
            inputType: 'input'
        },
        {
            property: nameof<Course>((m) => m.canBeGraded),
            inputType: 'select',
            clearButton: true,
            selectOptions: of([
                {
                    text: 'Gradable',
                    value: true
                },
                {
                    text: 'Non-gradable',
                    value: false
                }
            ])
        },
        {
            property: nameof<Course>((m) => m.enrollment),
            label: 'Has Enrollment',
            inputType: 'select',
            clearButton: true,
            selectOptions: of([
                {
                    text: 'Yes',
                    value: true
                },
                {
                    text: 'No',
                    value: false
                }
            ])
        }
    ];
    initialFilter: FilterSearchCriteria = {
        alias: 'course_list_registrar_report'
    };
    criteria: TableSearchCriteria = {
        sortBy: nameof<Course>((m) => m.termCode),
        sortDirection: 'desc'
    };
    columns: Array<ColumnDefinition> = [
        {
            property: nameof<Course>((m) => m.id)
        },
        {
            property: nameof<Course>((m) => m.crn),
            headerLabel: 'CRN',
            alternateExportPath: 'crn',
            cellTooltip: `Click to view the course roster for ${nameofExt<Course>((m) => m.crn)}`,
            underlineBlue: true,
            clickCallBack: {
                this: this,
                function: (event: ColumnClickEvent<Course>) => {
                    this.dialogService.show(
                        {
                            termCode: event.element.termCode,
                            crn: event.element.crn,
                            readonly: true
                        },
                        {
                            component: RosterComponent,
                            dialogAlias: 'roster',
                            uniqueId: this.dialogService.generateUniqueId(event.element, 'id', 'roster'),
                            title: `${event.element.crn} - ${event.element.termCodeDescription} - ${event.element.courseTitle}`
                        }
                    );
                    return {
                        completed: true
                    };
                }
            }
        },
        {
            property: nameof<Course>((m) => m.termCodeDescription),
            headerLabel: 'Term',
            alternateExportPath: 'termCode'
        },
        {
            property: nameof<Course>((m) => m.subjectCode)
        },
        {
            property: nameof<Course>((m) => m.courseNumber)
        },
        {
            property: nameof<Course>((m) => m.sectionNumber)
        },
        {
            property: nameof<Course>((m) => m.courseTitle),
            cellWrapperCss: 'padding-right-sm'
        },
        {
            property: nameof<Course>((m) => m.department)
        },
        {
            property: nameof<Course>((m) => m.enrollment),
        },
        {
            property: nameof<Course>((m) => m.gradeDelayCount),
            headerLabel: 'Faculty Issued Grade Delay',
            disableSort: true
        },
        {
            property: nameof<Course>((m) => m.canBeGraded),
            booleanAsYesNo: true
        },
        {
            property: nameof<Course>((m) => m.gradeSubmitted),
            booleanAsYesNo: true,
            disableSort: true
        },
        {
            property: nameof<Course>((m) => m.gradeFirstSubmittedBy),
            disableSort: true,
            alternateExportPath: 'firstGradeCourseRoster.gradeSubmittedBy'
        },
        {
            property: nameof<Course>((m) => m.gradeFirstSubmittedByNetId),
            alternateExportPath: 'firstGradeCourseRoster.gradeSubmitNetId'
        },
        {
            property: nameof<Course>((m) => m.gradeFirstSubmittedDate),
            isDate: true,
            dateFormat: 'short',
            disableSort: true,
            alternateExportPath: 'firstGradeCourseRoster.gradeSubmitDate'
        },
        {
            property: 'actions',
            actions: [
                {
                    discriminator: 'related',
                    color: 'primary',
                    icon: 'person_search',
                    toolTip: `Click to view all instructors for CRN: ${nameofExt<Course>((m) => m.crn)}`,
                    clickCallBack: {
                        this: this,
                        function: (event: ActionButtonClickEvent<Course>) => {
                            this.dialogService.show(
                                {
                                    crn: event.element.crn,
                                    termCode: event.element.termCode,
                                    readonly: false
                                },
                                {
                                    component: CourseListRegistrarInstructorComponent,
                                    dialogAlias: 'courseListRegistrarInstructor',
                                    title: `Instructors for CRN: ${event.element.crn} ${event.element.courseTitle}`,
                                    uniqueId: `instructors_${event.element.crn}_${event.element.termCode}`
                                }
                            );
                            return {
                                completed: true
                            };
                        }
                    }
                },
                {
                    discriminator: 'related',
                    color: 'primary',
                    icon: 'assignment',
                    toolTip: `Click to view logs for CRN: ${nameofExt<Course>((m) => m.crn)}`,
                    showCallback: {
                        this: this,
                        function: (element: Course) => {
                            return this.isRegAdminStaff;
                        }
                    },
                    clickCallBack: {
                        this: this,
                        function: (event: ActionButtonClickEvent<Course>) => {
                            this.dialogService.show(
                                {
                                    termCode: event.element.termCode,
                                    crn: event.element.crn,
                                    readonly: false
                                },
                                {
                                    component: ViewLogComponent,
                                    dialogAlias: 'view_log',
                                    title: `${event.element.courseTitle} (CRN: ${event.element.crn}) - ${event.element.termCodeDescription}`,
                                    uniqueId: `view_log_${event.element.crn}_${event.element.termCode}`
                                }
                            );
                            return {
                                completed: true
                            };
                        }
                    }
                }
            ]
        }
    ];
    constructor(
        private ui: RowUiService,
        private service: CourseService,
        private router: Router,
        private dialogService: DialogService<CourseDialogData>,
        private security: UcrSecurityService
    ) {
        this.security.getUserInfo().subscribe((userInfo: UserInfo) => {
            this.isRegAdminStaff = userInfo?.ifAnyGranted([Roles.registrarAdmin, Roles.registrarStaff]);
        });
    }

    ngOnInit(): void {
        this.ui.removeRowClass();
    }

    ngOnDestroy() {
        this.ui.addRowClass();
    }
}
